import React, { useState, useEffect, forwardRef } from 'react';
import { Star as _Star, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

/**
 * ReviewsSection Component
 * Displays reviews with rating breakdown, filtering, and review list
 *
 * Fetches real review data from API (MVP 1.18)
 *
 * @param {string} entityType - 'experience' or 'guide'
 * @param {string} entityId - Experience or Guide ID
 * @param {Function} onWriteReview - Callback when user clicks "Write a review"
 * @param {boolean} showAllLink - Show "Show all reviews" button
 * @param {Function} onStatsLoaded - Callback when stats are loaded (optional)
 */
const ReviewsSection = forwardRef(({
  entityType = 'experience', // 'experience' or 'guide'
  entityId,
  onWriteReview,
  showAllLink = true,
  onStatsLoaded
}, ref) => {
  const theme = useTheme();
  const [selectedRatingFilter, setSelectedRatingFilter] = useState(null);
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Category mapping from API keys to display labels
  const categoryLabels = {
    safety: 'Safety & Professionalism',
    knowledge: 'Guide Knowledge & Engagement',
    communication: 'Communication & Responsiveness',
    value: 'Value for Money'
  };

  // Fetch stats
  useEffect(() => {
    if (!entityId) {return;}

    const fetchStats = async () => {
      try {
        const endpoint = entityType === 'experience'
          ? `/api/v1/reviews/experiences/${entityId}/stats`
          : `/api/v1/reviews/guides/${entityId}/stats`;

        const response = await fetch(endpoint);
        if (!response.ok) {throw new Error('Failed to fetch stats');}

        const data = await response.json();
        setStats(data.data);

        // Notify parent component if callback provided
        if (onStatsLoaded) {
          onStatsLoaded(data.data);
        }
      } catch (err) {
        console.error('Error fetching review stats:', err);
        setError(err.message);
      }
    };

    fetchStats();
  }, [entityId, entityType, onStatsLoaded]);

  // Fetch reviews
  useEffect(() => {
    if (!entityId) {return;}

    const fetchReviews = async () => {
      try {
        setLoading(true);
        const endpoint = entityType === 'experience'
          ? `/api/v1/reviews/experiences/${entityId}`
          : `/api/v1/reviews/guides/${entityId}`;

        const response = await fetch(`${endpoint}?page=${page}&limit=10`);
        if (!response.ok) {throw new Error('Failed to fetch reviews');}

        const data = await response.json();
        setReviews(data.data.reviews);
        setTotalPages(data.data.pagination.totalPages);
        setError(null);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [entityId, entityType, page]);

  // Handle helpful vote
  const handleVote = async (reviewId, isHelpful) => {
    try {
      const endpoint = isHelpful
        ? `/api/v1/reviews/${reviewId}/vote/helpful`
        : `/api/v1/reviews/${reviewId}/vote/not-helpful`;

      const response = await fetch(endpoint, { method: 'POST' });
      if (!response.ok) {throw new Error('Failed to vote');}

      // Refresh reviews to show updated counts
      // TODO: Optimistic update instead of full refresh
      const refreshEndpoint = entityType === 'experience'
        ? `/api/v1/reviews/experiences/${entityId}`
        : `/api/v1/reviews/guides/${entityId}`;

      const refreshResponse = await fetch(`${refreshEndpoint}?page=${page}&limit=10`);
      const refreshData = await refreshResponse.json();
      setReviews(refreshData.data.reviews);
    } catch (err) {
      console.error('Error voting on review:', err);
    }
  };

  // Show loading state
  if (loading && !stats) {
    return (
      <div ref={ref} className="bg-white rounded-xl p-6 shadow-sm mb-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !stats) {
    return (
      <div ref={ref} className="bg-white rounded-xl p-6 shadow-sm mb-6">
        <h2 className="text-xl font-bold mb-4">Customer Reviews</h2>
        <div className="text-red-600">Failed to load reviews. Please try again later.</div>
      </div>
    );
  }

  // Use stats data or defaults
  const averageRating = stats?.averageRating || 0;
  const totalReviews = stats?.totalReviews || 0;
  const ratingDistribution = stats?.ratingDistribution || { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
  const categoryAverages = stats?.categoryAverages || {};

  // Convert category averages to array for display
  const categoryRatings = Object.entries(categoryAverages).map(([key, rating]) => ({
    category: categoryLabels[key] || key,
    rating: parseFloat(rating)
  }));

  const filteredReviews = selectedRatingFilter
    ? reviews.filter(review => review.overallRating === selectedRatingFilter)
    : reviews;

  if (loading) {
    return (
      <div ref={ref} className="bg-white rounded-xl p-6 shadow-sm mb-6">
        <h2 className="text-xl font-bold mb-6">Customer Reviews</h2>
        <div className="text-center py-12">
          <p className="text-gray-600">Loading reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="bg-white rounded-xl p-6 shadow-sm mb-6">
      <h2 className="text-xl font-bold mb-6">Customer Reviews</h2>

      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">No reviews yet. Be the first to review!</p>
          {onWriteReview && (
            <button
              onClick={onWriteReview}
              className={`${theme.buttonPrimary} px-6 py-2 rounded-lg`}
            >
              Write a review
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-5xl font-bold">{stats?.averageRating ? Number(stats.averageRating).toFixed(2) : '0.00'}</span>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <_Star
                        key={star}
                        className={`w-5 h-5 ${
                          star <= Math.round(averageRating)
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="text-sm text-gray-600">{totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}</div>
                </div>
              </div>

              <div className="space-y-2 mt-4">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = ratingDistribution[rating.toString()] || 0;
                  const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                  const isSelected = selectedRatingFilter === rating;

                  return (
                    <button
                      key={rating}
                      onClick={() => setSelectedRatingFilter(isSelected ? null : rating)}
                      className={`w-full flex items-center gap-3 text-sm hover:bg-gray-50 py-1 px-2 rounded transition ${
                        isSelected ? 'bg-gray-100' : ''
                      }`}
                    >
                      <span className={`${theme.textPrimary} ${theme.textHover} underline w-12 text-left`}>
                        {rating} star
                      </span>
                      <div className="flex-1 h-5 bg-gray-200 rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-amber-400 transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className={`${theme.textPrimary} ${theme.textHover} underline w-12 text-right`}>
                        {percentage.toFixed(0)}%
                      </span>
                    </button>
                  );
                })}
              </div>

              {selectedRatingFilter && (
                <button
                  onClick={() => setSelectedRatingFilter(null)}
                  className={`mt-3 text-sm ${theme.textPrimary} ${theme.textHover} underline`}
                >
                  Clear filter
                </button>
              )}
            </div>

            {categoryRatings.length > 0 && (
              <div>
                <h3 className="font-semibold mb-4">Rating Breakdown</h3>
                <div className="space-y-3">
                  {categoryRatings.map((item, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700">{item.category}</span>
                        <span className="text-sm font-semibold">{item.rating.toFixed(1)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1">
                        <div
                          className={`${theme.buttonPrimary.split(' ')[0]} h-1 rounded-full`}
                          style={{ width: `${(item.rating / 5) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {onWriteReview && (
            <div className="border-t pt-6 mb-6">
              <h3 className="font-semibold text-base mb-3">Review this {entityType}</h3>
              <div className="text-sm text-gray-600 mb-4">
                Share your thoughts with other adventurers
              </div>
              <button
                onClick={onWriteReview}
                className="py-2 px-6 border-2 border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition text-sm"
              >
                Write a review
              </button>
            </div>
          )}

          <div className="space-y-6">
            {filteredReviews.map((review) => (
              <div key={review.reviewId} className="border-b pb-6 last:border-b-0">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-lg font-semibold text-gray-600">
                    {/* TODO: Replace with actual user avatar once user profiles are integrated */}
                    {review.reviewerId?.substring(0, 2).toUpperCase() || '??'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-semibold">
                          {/* TODO: Replace with actual user name once user profiles are integrated */}
                          Customer Review
                        </div>
                        <div className="text-sm text-gray-600">
                          {new Date(review.publishedAt || review.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <_Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= review.overallRating
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>

                    {review.title && (
                      <h4 className="font-semibold mb-2">{review.title}</h4>
                    )}

                    {review.comment && (
                      <p className="text-gray-700 mb-3">{review.comment}</p>
                    )}

                    {/* Helpful votes */}
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <button
                        onClick={() => handleVote(review.reviewId, true)}
                        className="flex items-center gap-1 hover:text-gray-900 transition"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        <span>Helpful ({review.helpfulCount || 0})</span>
                      </button>
                      <button
                        onClick={() => handleVote(review.reviewId, false)}
                        className="flex items-center gap-1 hover:text-gray-900 transition"
                      >
                        <ThumbsDown className="w-4 h-4" />
                        <span>Not helpful ({review.notHelpfulCount || 0})</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedRatingFilter ? (
            <div className="mt-6 text-center text-sm text-gray-600">
              Showing {filteredReviews.length} {selectedRatingFilter}-star {filteredReviews.length === 1 ? 'review' : 'reviews'}
            </div>
          ) : showAllLink && totalPages > 1 ? (
            <div className="mt-6 flex justify-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="py-2 px-4 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="py-2 px-4 text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="py-2 px-4 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
});

ReviewsSection.displayName = 'ReviewsSection';

export default ReviewsSection;
